// frontend/src/components/ads/RSVPSuccessModal.js
// Success modal shown after RSVP with optional sponsored content

import React, { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

/**
 * RSVPSuccessModal - Shown after successful RSVP with confirmation ads
 * 
 * Props:
 * - teeTime: The tee time the player just RSVP'd to
 * - ads: Array of confirmation ads to display
 * - organizationId: For tracking
 * - playerId: For tracking
 * - onClose: Callback when modal is closed
 * - formatDate: Utility function for date formatting
 * - formatTime: Utility function for time formatting
 */
const RSVPSuccessModal = ({ 
  teeTime, 
  ads = [], 
  organizationId, 
  playerId, 
  onClose,
  formatDate,
  formatTime 
}) => {
  const hasTrackedImpressions = useRef(false);

  // Track impressions for all visible ads
  useEffect(() => {
    if (hasTrackedImpressions.current || ads.length === 0) return;

    const trackImpressions = async () => {
      for (const ad of ads) {
        try {
          await fetch(`${API_URL}/ads/impression`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              adId: ad.id,
              organizationId,
              playerId,
              placement: 'confirmation'
            })
          });
        } catch (err) {
          console.error('Failed to track confirmation ad impression:', err);
        }
      }
      hasTrackedImpressions.current = true;
    };

    trackImpressions();
  }, [ads, organizationId, playerId]);

  const handleAdClick = (ad) => {
    const clickUrl = `${API_URL}/ads/click/${ad.id}?organizationId=${organizationId || ''}&playerId=${playerId || ''}`;
    window.open(clickUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          maxWidth: 420,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        {/* Success Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            padding: 24,
            textAlign: 'center',
            borderRadius: '16px 16px 0 0'
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
            You're In!
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>
            Your spot has been reserved
          </div>
        </div>

        {/* Tee Time Details */}
        <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#111827' }}>
            {teeTime?.course || teeTime?.course_name || 'Golf Course'}
          </div>
          <div style={{ display: 'flex', gap: 16, color: '#6b7280', fontSize: 14 }}>
            <span>📅 {formatDate ? formatDate(teeTime?.date) : teeTime?.date}</span>
            <span>⏰ {formatTime ? formatTime(teeTime?.time) : teeTime?.time}</span>
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
            💡 Tip: Add this to your calendar so you don't forget!
          </div>
        </div>

        {/* Sponsored Content (if ads exist) */}
        {ads.length > 0 && (
          <div style={{ padding: 16 }}>
            <div style={{ 
              fontSize: 10, 
              color: '#9ca3af', 
              textTransform: 'uppercase', 
              letterSpacing: '0.5px',
              marginBottom: 12,
              fontWeight: 600
            }}>
              Sponsored
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {ads.slice(0, 1).map((ad) => (
                <div
                  key={ad.id}
                  onClick={() => handleAdClick(ad)}
                  style={{
                    background: `linear-gradient(135deg, ${ad.bgColorFrom || '#16a34a'} 0%, ${ad.bgColorTo || '#15803d'} 100%)`,
                    borderRadius: 10,
                    padding: 14,
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    position: 'relative'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  {/* Badge */}
                  {ad.badge && (
                    <div style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      fontSize: 9,
                      fontWeight: 700,
                      color: '#fff',
                      background: 'rgba(0,0,0,0.25)',
                      padding: '2px 6px',
                      borderRadius: 8
                    }}>
                      {ad.geoScope === 'local' && '📍 '}{ad.badge}
                    </div>
                  )}
                  
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>
                    {ad.sponsor}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
                    {ad.headline}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.9)', marginBottom: 8 }}>
                    {ad.description}
                  </div>
                  <div style={{ 
                    display: 'inline-block',
                    background: '#fff',
                    color: ad.bgColorFrom || '#16a34a',
                    padding: '6px 14px',
                    borderRadius: 16,
                    fontSize: 12,
                    fontWeight: 600
                  }}>
                    {ad.ctaText || 'Learn More'} →
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div style={{ padding: 16, paddingTop: ads.length > 0 ? 0 : 16 }}>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: 14,
              background: '#111827',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#374151'}
            onMouseOut={(e) => e.target.style.background = '#111827'}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default RSVPSuccessModal;
